import { orderBy } from 'lodash';
import { AxiosPromise, AxiosResponse } from 'axios';

import {
  DocumentType,
  File,
  FileAttachment,
  FileError,
  FilePayment,
  Odontogram,
  ReceiptType,
  Resolution,
  SimpleFileAttachment,
  TramitType,
  TransferType,
  FileCoverage,
  VoucherType,
  Disposition,
  Resource,
  Coverage,
  Documentation,
  FilePartialPayment,
  FileScope,
  TransferFileResponse,
  TransferFileRequest,
  IPartialPaymentRequest,
  InboxDetail,
  IAuthorizationRequest,
  IAuthorization,
  IAuthorizationValidate,
  TNotificationRequestTarget,
} from '@/interfaces/files';

import { CBU } from '@/interfaces/cbu';
import { Note } from '@/interfaces/note';
import { PaginatedResponse } from '@/interfaces/response';
import { number } from 'prop-types';
import request from '@/umi-request';

export async function getFiles(params: any) {
  return request({
    url: 'files/',
    method: 'GET',
    params,
  }) as AxiosPromise<File>;
}

export async function getFilesShort(search: number | string) {
  return request({
    url: 'files/short/',
    method: 'GET',
    params: { search }
  }) as AxiosPromise<File>;
}

export async function searchFiles(params: any) {
  return request({
    url: 'files/search/',
    method: 'GET',
    params,
  }) as AxiosPromise<File>;
}

export async function getFilesCounters(params: any) {
  return request({
    url: 'files/counters/',
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getRelatedFiles(params: any) {
  return request({
    url: 'users/me/files/',
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getFile(id: number) {
  return request({
    url: `files/${id}/`,
    method: 'GET'
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function uploadFileValidationAttachment(data: any) {
  return request({
    url: `files/upload-validation-attachment/`,
    method: 'POST',
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function getAffiliateFile(affiliate: number, id: number) {
  return request({
    url: `affiliates/${affiliate}/files/${id}/`,
    method: 'GET'
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getFilePayments(id: number, params: any) {
  return request({
    url: `files/${id}/payments/`,
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getFilePayment(id: number, id_pay = number) {
  return request({
    url: `files/${id}/payments/${id_pay}/update/`,
    method: 'GET'
  }) as AxiosPromise<FilePayment>;
}

export function registerFilePayments(id: number, data: any) {
  return request({
    url: `files/${id}/payments/?many=true`,
    method: 'POST',
    data
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getFileAttachemnts(affiliate_id: number, file_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/attachments/`,
    method: 'GET'
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getTramitAttachments(file_id: number) {
  return request<any>(`files/${file_id}/tramit/attachments/`);
}

export async function getFileTicketAttachemnts(affiliate_id: number, file_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/ticket-attachments/`,
    method: 'GET',
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getFileReceiptAttachemnts(affiliate_id: number, file_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/receipt-attachments/`,
    method: 'GET',
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getFileVoucherAttachemnts(affiliate_id: number, file_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/voucher-attachments/`,
    method: 'GET',
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getFileOdontograms(affiliate_id: number, file_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/odontograms/`,
    method: 'GET',
  }) as AxiosPromise<Odontogram>;
}

export async function createCoverages(file_id: number, data: any) {
  return request({
    url: `files/${file_id}/approve/`,
    method: 'PUT',
    data
  }) as AxiosPromise<Resolution>;
}

export async function getCoverages(file_id: number, disposition_id: number | null) {
  return request({
    url: `files/${file_id}/file-coverage`,
    method: 'GET',
  }) as AxiosPromise<PaginatedResponse<Coverage>>;
}

export async function getCoverage(file_id: number, disposition_id: number | null, coverage_id: number) {
  return request({
    url: `files/${file_id}/file-coverage/${coverage_id}/`,
    method: 'GET',
  }) as AxiosPromise<Coverage>;
}

export async function getCoveragesPerFile(file_id: number) {
  return request({
    url: `files/${file_id}/file-coverages/`,
    method: 'GET',
  }) as AxiosPromise<FileCoverage[]>;
}

export async function getDocumentsByCoverage(file_id: number, disposition_id: number | null, coverage_id: number) {
  return request({
    url: `files/${file_id}/file-coverage/${coverage_id}/documents-ranges/`,
    method: 'GET',
  }) as AxiosPromise<Documentation>;
}

export async function createPartialPayment(file_id: number, disposition_id: number | null, coverage_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-coverage/${coverage_id}/partial-payment/`,
    method: 'POST',
    data
  }) as AxiosPromise<FilePartialPayment>;
}

export async function updatePartialPayment(file_id: number, disposition_id: number | null, coverage_id: number, partialPayment_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-coverage/${coverage_id}/partial-payment/${partialPayment_id}/`,
    method: 'PUT',
    data
  }) as AxiosPromise<FilePartialPayment>;
}

export async function changePartialPaymentState(file_id: number, disposition_id: number | null, coverage_id: number, partial_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-coverage/${coverage_id}/partial-payment/${partial_id}/change-state/`,
    method: 'PUT',
    data
  }) as AxiosPromise<FilePartialPayment>;
}

export async function validatePartialPayment(file_id: number, disposition_id: number | null, coverage_id: number, partial_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-coverage/${coverage_id}/partial-payment/${partial_id}/validate/`,
    method: 'PUT',
    data
  }) as AxiosPromise<FilePartialPayment>;
}

export async function getDispositions(file: number) {
  return request({
    url: `files/${file}/file-scope`,
    method: 'GET',
  }) as AxiosPromise<PaginatedResponse<Disposition>>;
}

export async function getDisposition(file: number, disposition_id: number) {
  return request({
    url: `files/${file}/file-scope/${disposition_id}`,
    method: 'GET',
  }) as AxiosPromise<Disposition>;
}

export async function uploadDisposition(file_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-scope/`,
    method: 'POST',
    data
  }) as AxiosPromise<FileScope>;
}

export async function updateDispositionAmount(file_id: number, disposition_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-scope/${disposition_id}/edit-amount/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Disposition>;
}

export async function changeDispositionState(file_id: number, disposition_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-scope/${disposition_id}/change-state/`,
    method: 'PUT',
    data
  }) as AxiosPromise<Disposition>;
}

export async function validateFilesScope(file_id: number, file_scope_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-scope/${file_scope_id}/validate_filescope/`,
    method: 'PUT',
    data
  })
}

export async function getScopeNextNumber(file_id: number, data: any) {
  return request({
    url: `files/${file_id}/file-scope/get-next-number/`,
    method: 'POST',
    data
  })
}

export async function uploadResource(file: number, disposition: number, data: any) {
  return request({
    url: `files/${file}/file-scope/${disposition}/file-resource/`,
    method: 'POST',
    data
  }) as AxiosPromise<Resource>;
}

export async function changeResourceState(file: number, disposition: number, resource: number, data: any) {
  return request({
    url: `files/${file}/file-scope/${disposition}/file-resource/${resource}/change-state/`,
    method: 'PUT',
    data
  }) as AxiosPromise<Resource>;
}

export async function createAuthorization(file: number, data: IAuthorizationRequest) {
  return request({
    url: `files/${file}/authorization/`,
    method: 'POST',
    data
  }) as AxiosPromise<IAuthorization>;
}

export async function updateAuthorization(file: number, authorization: number, optical_name: string) {
  return request({
    url: `files/${file}/authorization/${authorization}/`,
    method: 'PATCH',
    data: {
      optical_name
    }
  }) as AxiosPromise<IAuthorization>;
}


export async function getAuthorization(file: number) {
  return request({
    url: `files/${file}/authorization/`,
    method: 'GET',
  }) as AxiosPromise<IAuthorization[]>;
}

export async function validateAuthorization(file: number, authorization: number, data: IAuthorizationValidate) {
  return request({
    url: `files/${file}/authorization/${authorization}/validate/`,
    method: 'PUT',
    data
  }) as AxiosPromise<IAuthorization>
}

export async function downloadAuthorization(file: number, authorization: number) {
  return request({
    url: `files/${file}/authorization/${authorization}/download_template/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function downloadDisposition(file: number, disposition: number) {
  return request({
    url: `files/${file}/file-scope/${disposition}/download_template/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;

}

export async function cancelAuthorization(file: number, authorization: number) {
  return request({
    url: `files/${file}/authorization/${authorization}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<any>;
}

export async function renewAuthorization(file: number, authorization: number) {
  return request({
    url: `files/${file}/authorization/${authorization}/renew/`,
    method: 'PUT',
  })
}

export async function notifyDocument(file: number, document: number, target: TNotificationRequestTarget) {
  return request({
    url: `files/${file}/${target}/${document}/send_email/`,
    method: 'POST',
  }) as AxiosPromise<any>;
}

export async function getFilesValidations(file_id: number) {
  return request({
    url: `files/${file_id}/file-validations/`,
    method: 'GET',
  }) as AxiosPromise<Resolution>;
}

export async function getFilesPrivateAttachments(file_id: number) {
  return request({
    url: `files/${file_id}/attachments/`,
    method: 'GET',
  }) as AxiosPromise<Resolution>;
}

export async function canRequestPartialPayment(file_id: number) {
  return request({
    url: `files/${file_id}/check-validation-partialpayment/`,
    method: 'GET',
  }) as AxiosPromise<any>;
}

export async function getFileZip(id: number) {
  return request({
    url: `files/${id}/download/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function getFileOnlyCover(id: number) {
  return request({
    url: `files/${id}/download_only_cover/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function uploadFileOdontogram(affiliate_id: number, file_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/odontograms/`,
    method: 'POST',
    data
  }) as AxiosPromise<Odontogram>;
}

export async function updateFileOdontogram(affiliate_id: number, file_id: number, odontrogram_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/odontograms/${odontrogram_id}/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Odontogram>;
}

export async function getTramitTypes() {
  return request({
    url: 'tramit-types/',
    method: 'GET',
  }) as AxiosPromise<TramitType[]>;
}

export async function getFileErrors() {
  return request({
    url: 'file-error-types/',
    method: 'GET',
  }) as AxiosPromise<FileError[]>;
}

export async function getTransferTypes() {
  return request({
    url: 'transfer-types/',
    method: 'GET',
  }) as AxiosPromise<TransferType[]>;
}

export async function sendFileNote(id: number | string, data: { content: string }) {
  return request({
    url: `files/${id}/notes/`,
    method: 'POST',
    data
  }) as AxiosPromise<Note<File>>;
}

export async function getFileNotes(id: number | string) {
  return request({
    url: `files/${id}/notes/`,
    method: 'GET',
  }) as AxiosPromise<Note<File>[]>;
}

export async function getFileChanges(id: number | string, params: any) {
  return request({
    url: `files/${id}/changes/`,
    method: 'GET',
    params
  }) as AxiosPromise<File[]>;
}

export async function getFileTranferChanges(id: number | string, params: any) {
  return request({
    url: `files/${id}/transfer-changes/`,
    method: 'GET',
    params
  }) as AxiosPromise<File[]>;
}

export async function createFile(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/`,
    method: 'POST',
    data
  }) as AxiosPromise<File>;
}

export async function createEmployeeFile(data: any) {
  return request({
    url: `files/`,
    method: 'POST',
    data
  }) as AxiosPromise<File>;
}

export async function updateFileAllowance(id: number, data: any) {
  return request({
    url: `files/${id}/change-allowance/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<File>;
}

// Edita sólo la cantidad solicitada (allowance_amount). El backend la valida contra
// las unidades disponibles de la prestación y sólo la permite en estados CREATED / PRESENTED.
// Devuelve el expediente serializado.
export async function changeFileAllowanceAmount(id: number | string, allowance_amount: number) {
  return request({
    url: `files/${id}/change-allowance-amount/`,
    method: 'PATCH',
    data: { allowance_amount },
  }) as AxiosPromise<File>;
}

export async function updateFilePeriod(id: number, data: any) {
  return request({
    url: `files/${id}/change-period/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<File>;
}

export async function hasPendingDocuments(id: number) {
  return request({
    url: `files/${id}/has-pending-documents/`,
    method: 'GET',
  }) as AxiosPromise<boolean>;
}

export async function updateFilePaymentData(id: number, data: any) {
  return request({
    url: `files/${id}/change-payment-data/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<File>;
}

export async function updateFileTramitType(id: number, data: any) {
  return request({
    url: `files/${id}/change-tramit-type/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<File>;
}

export async function getDocumentTypes() {
  return request({
    url: `document-types/`,
    method: 'GET',
  }).then((results: AxiosResponse<DocumentType[]>) => (
    orderBy(results.data, 'name', 'asc')
  )) as Promise<DocumentType[]>;
}

export function getReceiptTypes() {
  return request({
    url: `receipt-types/`,
    method: 'GET',
  }) as AxiosPromise<ReceiptType[]>;/* .then((results : AxiosResponse<ReceiptType[]>) => {
    orderBy(results, 'name', 'asc')
  }) */
}

export function getVoucherTypes() {
  return request({
    url: `voucher-types/`,
    method: 'GET',
  }) as AxiosPromise<VoucherType[]>; /* .then((results : AxiosResponse<VoucherType[]>) => {
    orderBy(results, 'name', 'asc')
  }) ; */
}

export async function uploadFileAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-attachment/`,
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data' },
    data
  }) as AxiosPromise<FileAttachment>;
}

export async function deleteFileAttachment(affiliate: number, file: number, id: number) {
  return request({
    url: `affiliates/${affiliate}/files/${file}/attachments/${id}/`,
    method: 'DELETE',
  }) as AxiosPromise;
}

export async function uploadVoucherFileAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-voucher-attachment/`,
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data' },
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadReceiptFileAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-receipt-attachment/`,
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data' },
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadTicketFileAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-ticket-attachment/`,
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data' },
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function createVoucherAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-voucher/`,
    method: 'POST',
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadVoucherAttachment(file_id: number | null, affiliate_id: number, data: any) {
  return file_id
    ? request({
      url: `affiliates/${affiliate_id}/files/${file_id}/voucher-attachments/`,
      method: 'POST',
      data
    }) as AxiosPromise<SimpleFileAttachment>
    : request({
      url: `affiliates/${affiliate_id}/files/upload-voucher/`,
      method: 'POST',
      data
    }) as AxiosPromise<SimpleFileAttachment>;
}

export async function updateVoucherAttachment(file_id: number, affiliate_id: number, attachment_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/voucher-attachments/${attachment_id}/`,
    method: 'PUT',
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function createReceiptAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-receipt/`,
    method: 'POST',
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadReceiptAttachment(file_id: number | null, affiliate_id: number, data: any) {
  return file_id
    ? request({
      url: `affiliates/${affiliate_id}/files/${file_id}/receipt-attachments/`,
      method: 'POST',
      data
    }) as AxiosPromise<SimpleFileAttachment>
    : request({
      url: `affiliates/${affiliate_id}/files/upload-receipt/`,
      method: 'POST',
      data
    }) as AxiosPromise<SimpleFileAttachment>;
}

export async function updateReceiptAttachment(file_id: number, affiliate_id: number, attachment_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/receipt-attachments/${attachment_id}/`,
    method: 'PUT',
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function createTicketAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-ticket/`,
    method: 'POST',
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadTicketAttachment(file_id: number | null, affiliate_id: number, data: any) {
  return file_id
    ? request({
      url: `affiliates/${affiliate_id}/files/${file_id}/ticket-attachments/`,
      method: 'POST',
      data
    }) as AxiosPromise<SimpleFileAttachment>
    : request({
      url: `affiliates/${affiliate_id}/files/upload-ticket/`,
      method: 'POST',
      data
    }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadBillingFileAttachment(payload: any, type: string) {
  const { affiliate, data } = payload;
  return request({
    url: `affiliates/${affiliate}/files/upload-${type}-attachment/`,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data,
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadBillingAttachment(payload: any, type: string) {
  const { affiliate, file, data } = payload;
  return request({
    url: `affiliates/${affiliate}/files/${file}/${type}-attachments/`,
    method: 'POST',
    data,
  },
  ) as AxiosPromise<SimpleFileAttachment>;
}

export async function updateAffiliatePartialFile(file_id: number, attachment_id: number, data: any) {
  return request({
    url: `files/${file_id}/tramit/${attachment_id}/update/`,
    method: 'PUT',
    data,
  }) as AxiosPromise<any>;
}

export async function updateTicketAttachment(file_id: number, affiliate_id: number, attachment_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/files/${file_id}/ticket-attachments/${attachment_id}/`,
    method: 'PUT',
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadCBUAttachment(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-cbu/`,
    method: 'POST',
    data
  }) as AxiosPromise<CBU>;
}

export async function getAffiliateCBUs(affiliate: number, data: any) {
  return request({
    url: `affiliates/${affiliate}/files/upload-cbu/`,
    method: 'POST',
    data
  }) as AxiosPromise<CBU[]>;
}

export async function rejectFile(file: number, data: any) {
  return request({
    url: `files/${file}/disapprove/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function requestMoreData(file: number, data: any) {
  return request({
    url: `files/${file}/request-more-data/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function openFile(file: number, data: any) {
  return request({
    url: `files/${file}/create/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function presentFile(file: number, data: any) {
  return request({
    url: `files/${file}/present/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function cancelFile(file: number, data: any) {
  return request({
    url: `files/${file}/cancel/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function preApproveFile(file: number, data: any) {
  return request({
    url: `files/${file}/pre-approve/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function preApproveFileConfirm(file: number) {
  return request({
    url: `files/${file}/pre-approve/`,
    method: 'PUT',
  }) as AxiosPromise<File>;
}

export async function approveFile(file: number, data: any) {
  return request({
    url: `files/${file}/approve/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function payFile(file: number, data: any) {
  return request({
    url: `files/${file}/pay/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function partialPayFile(file: number, data: any) {
  return request({
    url: `files/${file}/partial-pay/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function reprocessFile(file: number, data: any) {
  return request({
    url: `files/${file}/refuse/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function acceptFile(file: number, data?: any) {
  return request({
    url: `files/${file}/accept/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function sendFileToValidate(file: number, data?: any) {
  return request({
    url: `files/${file}/request-validation/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function validateFile(file: number, data?: any) {
  return request({
    url: `files/${file}/validate/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function releaseFile(file: number, data: any) {
  return request({
    url: `files/${file}/release/`,
    method: 'PUT',
    data
  }) as AxiosPromise<File>;
}

export async function transferFile(data: TransferFileRequest) {
  return request({
    url: `files/transfer-outbox-files/`,
    method: 'PUT',
    data
  }) as AxiosPromise<TransferFileResponse>;
}

export async function getFilePaymentsRequest(file_id: number) {
  return request({
    url: `files/${file_id}/payment-requested/`,
    method: 'GET',
  }) as AxiosPromise<IPartialPaymentRequest[]>;
}

export async function getInbox(id: number) {
  return request({
    url: `inboxes/${id}/detail/`,
    method: 'GET',
  }) as AxiosPromise<InboxDetail>;
}

export async function getInboxes(params: any) {
  return request({
    url: 'inboxes/',
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getInboxFileTransfer(id: number, params: any) {
  return request({
    url: `inboxes/${id}/files/`,
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getInboxSendFileTransferPDF(id: number, data: any) {
  return request({
    url: `inboxes/${id}/sent_Files_Inbox_PDF/`,
    method: 'POST',
    responseType: 'blob',
    data,
  }) as AxiosPromise<PaginatedResponse<File>>;
}

// Stadistics

export async function getFilesPKIs() {
  return request({
    url: `file-statistics/files-pkis/`,
    method: 'GET',
  }) as AxiosPromise<File>;
}

export async function getFilesTopLocations(params: any) {
  return request({
    url: `file-statistics/top-locations/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesTopBenefits(params: any) {
  return request({
    url: `file-statistics/top-benefits/`,
    method: 'GET',
    params
  }) as AxiosPromise;
}

export async function getFilesTopBenefitsByImport(params: any) {
  return request({
    url: `file-statistics/top-benefits-by-import/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesTopBenefitsMonthly(params: any) {
  return request({
    url: `file-statistics/top-benefits-monthly/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesTopAllowances(params: any) {
  return request({
    url: `file-statistics/top-allowances/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesTopAllowancesByImport(params: any) {
  return request({
    url: `file-statistics/top-allowances-by-import/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesTopAllowancesMonthly(params: any) {
  return request({
    url: `file-statistics/top-allowances-monthly/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesByType(params: any) {
  return request({
    url: `file-statistics/files-by-type/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesByEmployee(params: any) {
  return request({
    url: `file-statistics/files-by-employee/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFilesByReceptor(params: any) {
  return request({
    url: `file-statistics/files-by-receptor/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getTransferTypeAverageDelay(params: any) {
  return request({
    url: `file-statistics/transfer-type-average-delay/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getTransferTypeAccumulation(params: any) {
  return request({
    url: `file-statistics/transfer-type-accumulation/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFileLifestampByAllowance(params: any) {
  return request({
    url: `file-statistics/files-lifespan-by-allowances/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getFileLifestampByBenefits(params: any) {
  return request({
    url: `file-statistics/files-lifespan-by-benefits/`,
    method: 'GET',
    params
  }) as AxiosPromise<File>;
}

export async function getAllFileScope(params: any) {
  return request({
    url: `/file-scope/`,
    method: 'GET',
    params
  }) as AxiosPromise<any>;
}


